"use strict";

const TURKISH_LOCALE = "tr-TR";

function normalizeText(value) {
  if (value === null || value === undefined) return "";

  return String(value)
    .trim()
    .toLocaleLowerCase(TURKISH_LOCALE)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/\s+/g, " ");
}

function normalizeDirection(direction, fallback = "desc") {
  const normalized = normalizeText(direction);
  if (["asc", "oldest", "lowest", "artan", "en eski"].includes(normalized)) {
    return "asc";
  }
  if (["desc", "newest", "highest", "azalan", "en yeni"].includes(normalized)) {
    return "desc";
  }
  return fallback === "asc" ? "asc" : "desc";
}

function getValueByPath(item, path) {
  return String(path)
    .split(".")
    .reduce((value, segment) => {
      if (value === null || value === undefined) return undefined;
      return value[segment];
    }, item);
}

function matchesSearch(item, search, fields = []) {
  const query = normalizeText(search);
  if (!query) return true;

  return fields.some((field) => {
    const value =
      typeof field === "function" ? field(item) : getValueByPath(item, field);
    return normalizeText(value).includes(query);
  });
}

function filterBySearch(items, search, fields) {
  const source = Array.isArray(items) ? items : [];
  return source.filter((item) => matchesSearch(item, search, fields));
}

function compareNames(left, right) {
  return String(left || "").localeCompare(String(right || ""), TURKISH_LOCALE, {
    sensitivity: "base",
  });
}

function normalizeId(value, fallback = "") {
  if (typeof value !== "string" && typeof value !== "number") {
    return fallback;
  }
  const normalized = String(value).trim();
  return normalized || fallback;
}

function sortAssignmentsByAssignedAt(assignments, direction = "desc") {
  const multiplier = normalizeDirection(direction) === "asc" ? 1 : -1;
  return [...(Array.isArray(assignments) ? assignments : [])].sort(
    (left, right) => {
      const leftTime = Date.parse(left && left.assigned_at) || 0;
      const rightTime = Date.parse(right && right.assigned_at) || 0;
      const dateComparison = (leftTime - rightTime) * multiplier;
      if (dateComparison !== 0) return dateComparison;
      return compareNames(left && left.name, right && right.name);
    }
  );
}

function sortUsersByCompetencyCount(users, direction = "desc") {
  const multiplier = normalizeDirection(direction) === "asc" ? 1 : -1;
  return [...(Array.isArray(users) ? users : [])].sort((left, right) => {
    const leftCount = Number(left && left.competency_count) || 0;
    const rightCount = Number(right && right.competency_count) || 0;
    const countComparison = (leftCount - rightCount) * multiplier;
    if (countComparison !== 0) return countComparison;
    return compareNames(left && left.full_name, right && right.full_name);
  });
}

function extractId(value) {
  if (value && typeof value === "object") {
    return normalizeId(value.id);
  }
  return normalizeId(value);
}

function getUserCompetencyIds(user) {
  if (Array.isArray(user && user.competency_ids)) {
    return user.competency_ids.map(extractId);
  }
  if (Array.isArray(user && user.competencies)) {
    return user.competencies.map(extractId);
  }
  return [];
}

function getComparableDepartmentValues(value) {
  const values = value && typeof value === "object"
    ? [value.id, value.name, value.title]
    : [value];

  return values.map(normalizeText).filter(Boolean);
}

function getUserCompetencyAssignments(user) {
  const source = Array.isArray(user && user.competency_assignments)
    ? user.competency_assignments
    : Array.isArray(user && user.competencies)
      ? user.competencies
      : [];

  return source
    .filter((assignment) => assignment && typeof assignment === "object")
    .map((assignment) => ({
      competencyId: extractId(
        assignment.competency_id !== undefined
          ? assignment.competency_id
          : assignment.id
      ),
      assignedAt: assignment.assigned_at,
    }));
}

function hasDateFilterValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function parseDateBoundary(value, endOfDay = false) {
  const match = String(value || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return Number.NaN;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0
  );
  const parsed = new Date(timestamp);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return Number.NaN;
  }

  return timestamp;
}

function filterAdminUsers(users, filters = {}) {
  const managerId = extractId(filters.managerId);
  const selectedDepartmentValues = getComparableDepartmentValues(
    filters.department
  );
  const hasStartDate = hasDateFilterValue(filters.startDate);
  const hasEndDate = hasDateFilterValue(filters.endDate);
  const startTime = hasStartDate
    ? parseDateBoundary(filters.startDate)
    : null;
  const endTime = hasEndDate
    ? parseDateBoundary(filters.endDate, true)
    : null;
  if (
    (hasStartDate && !Number.isFinite(startTime)) ||
    (hasEndDate && !Number.isFinite(endTime)) ||
    (hasStartDate && hasEndDate && startTime > endTime)
  ) {
    return [];
  }
  const hasSingleCompetencyFilter =
    filters.competencyIds !== null &&
    filters.competencyIds !== undefined &&
    filters.competencyIds !== "";
  const selectedCompetencies = (
    Array.isArray(filters.competencyIds)
      ? filters.competencyIds
      : hasSingleCompetencyFilter
      ? [filters.competencyIds]
      : []
  )
    .map(extractId)
    .filter(Boolean);

  return (Array.isArray(users) ? users : []).filter((user) => {
    const searchMatches = matchesSearch(user, filters.search, [
      (item) => [
        item && item.full_name,
        item && item.fullName,
        item && item.fullname,
        item && item.first_name,
        item && item.last_name,
        item && item.name,
        item && item.surname,
      ].filter(Boolean).join(" "),
    ]);
    const managerMatches =
      !managerId || extractId(user.parent_id) === managerId;
    const userDepartmentValues = [
      ...getComparableDepartmentValues(user && user.department),
      ...getComparableDepartmentValues(
        user && user.organization && user.organization.department
      ),
    ];
    const departmentMatches =
      selectedDepartmentValues.length === 0 ||
      selectedDepartmentValues.some((value) =>
        userDepartmentValues.includes(value)
      );
    const competencyIds = getUserCompetencyIds(user);
    const competencyMatches =
      selectedCompetencies.length === 0 ||
      selectedCompetencies.some((id) => competencyIds.includes(id));
    const dateFilterActive = hasStartDate || hasEndDate;
    const dateMatches = !dateFilterActive || getUserCompetencyAssignments(user)
      .filter(
        (assignment) =>
          selectedCompetencies.length === 0 ||
          selectedCompetencies.includes(assignment.competencyId)
      )
      .some((assignment) => {
        const assignedTime = Date.parse(assignment.assignedAt);
        if (!Number.isFinite(assignedTime)) return false;
        return (
          (!hasStartDate || assignedTime >= startTime) &&
          (!hasEndDate || assignedTime <= endTime)
        );
      });

    return (
      searchMatches &&
      managerMatches &&
      departmentMatches &&
      competencyMatches &&
      dateMatches
    );
  });
}

function filterCommentsForViewer(comments, viewer = "employee") {
  const source = Array.isArray(comments) ? comments : [];
  if (viewer === "admin") return [...source];
  if (!["manager", "employee"].includes(viewer)) return [];

  const supportedComments = source.filter(
    (comment) =>
      comment && ["manager", "employee", "hr"].includes(comment.author_type)
  );
  if (viewer === "manager") {
    return supportedComments;
  }

  return supportedComments.filter(
    (comment) => comment.visible_to_employee === true
  );
}

function sortCommentsForDisplay(comments) {
  return [...(Array.isArray(comments) ? comments : [])].sort((left, right) => {
    const leftTime = Date.parse(left && left.created_at) || 0;
    const rightTime = Date.parse(right && right.created_at) || 0;
    if (leftTime !== rightTime) return leftTime - rightTime;

    return String((left && left.id) ?? "").localeCompare(
      String((right && right.id) ?? ""),
      "en",
      { numeric: true }
    );
  });
}

function paginateItems(items, page = 1, perPage = 10) {
  const source = Array.isArray(items) ? items : [];
  const safePerPage = Math.min(
    100,
    Math.max(1, Number.parseInt(perPage, 10) || 10)
  );
  const lastPage = Math.max(1, Math.ceil(source.length / safePerPage));
  const safePage = Math.min(
    lastPage,
    Math.max(1, Number.parseInt(page, 10) || 1)
  );
  const start = (safePage - 1) * safePerPage;

  return {
    data: source.slice(start, start + safePerPage),
    total: source.length,
    page: safePage,
    perPage: safePerPage,
    lastPage,
  };
}

function normalizeCommentText(text, maxLength = 1000) {
  if (typeof text !== "string") {
    throw new TypeError("Yorum metni bir metin olmalıdır.");
  }

  const normalized = text.trim();
  if (normalized.length < 1 || normalized.length > maxLength) {
    const error = new RangeError(
      `Yorum 1-${maxLength} karakter arasında olmalıdır.`
    );
    error.code = "INVALID_COMMENT";
    throw error;
  }

  return normalized;
}

function normalizeGoalText(text, maxLength = 1000) {
  if (typeof text !== "string") {
    throw new TypeError("Hedef metni bir metin olmalıdır.");
  }

  const normalized = text.trim();
  if (normalized.length < 1 || normalized.length > maxLength) {
    const error = new RangeError(
      `Hedef 1-${maxLength} karakter arasında olmalıdır.`
    );
    error.code = "INVALID_GOAL";
    throw error;
  }

  return normalized;
}

function createEmployeeComment(existingComments, commentInput = {}) {
  const userCompetencyId = normalizeId(commentInput.userCompetencyId);
  const commentId = normalizeId(commentInput.commentId);
  const authorId = normalizeId(commentInput.authorId);
  if (!userCompetencyId || !commentId || !authorId) {
    const error = new TypeError("Çalışan yorumu bilgileri geçersiz.");
    error.code = "INVALID_COMMENT_CONTEXT";
    throw error;
  }

  const authorName = String(commentInput.authorName || "").trim();
  const authorImage = String(commentInput.authorImage || "").trim();

  return {
    id: commentId,
    user_competency_id: userCompetencyId,
    author_id: authorId,
    author_name: authorName,
    author_image: authorImage,
    author: {
      id: authorId,
      full_name: authorName,
      image: authorImage,
    },
    author_type: "employee",
    text: normalizeCommentText(commentInput.text, 1000),
    created_at: String(commentInput.createdAt || ""),
    visible_to_employee: true,
  };
}

function cloneValue(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

module.exports = {
  cloneValue,
  createEmployeeComment,
  filterAdminUsers,
  filterBySearch,
  filterCommentsForViewer,
  matchesSearch,
  normalizeCommentText,
  normalizeGoalText,
  normalizeDirection,
  normalizeId,
  normalizeText,
  paginateItems,
  sortAssignmentsByAssignedAt,
  sortCommentsForDisplay,
  sortUsersByCompetencyCount,
};
